/**
 * Utility functions to extract specific sections from transaction logs
 * Makes it easier to access nested log data consistently across components
 */

/**
 * Extract Policy Router request data from selected log
 * @param {Object} selectedLog - The complete selected log object
 * @returns {Object|null} Router request data or null if not available
 */
export const getRouterRequest = (selectedLog) => {
  return selectedLog?.policyRouterLog?.routerRequest || null;
};

/**
 * Extract Policy Router response data from selected log
 * @param {Object} selectedLog - The complete selected log object
 * @returns {Object|null} Router response data or null if not available
 */
export const getRouterResponse = (selectedLog) => {
  return selectedLog?.policyRouterLog?.routerResponse || null;
};

/**
 * Extract Wrapper API request data from selected log
 * @param {Object} selectedLog - The complete selected log object
 * @returns {Object|null} Wrapper request data or null if not available
 */
export const getWrapperRequest = (selectedLog) => {
  return selectedLog?.policyWrapperApiLog?.wrapperRequest || null;
};

/**
 * Extract Wrapper API response data from selected log
 * @param {Object} selectedLog - The complete selected log object
 * @returns {Object|null} Wrapper response data or null if not available
 */
export const getWrapperResponse = (selectedLog) => {
  return selectedLog?.policyWrapperApiLog?.wrapperResponse || null;
};

/**
 * Extract Wrapper API source requests from selected log
 * @param {Object} selectedLog - The complete selected log object
 * @returns {Array|null} Array of source request data or null if not available
 */
export const getSourceRequests = (selectedLog) => {
  return selectedLog?.policyWrapperApiLog?.sourceRequest || null;
};

/**
 * Extract Wrapper API source responses from selected log
 * @param {Object} selectedLog - The complete selected log object
 * @returns {Array|null} Array of source response data or null if not available
 */
export const getSourceResponses = (selectedLog) => {
  return selectedLog?.policyWrapperApiLog?.sourceResponse || null;
};

/**
 * Check if Policy Router API data exists in log
 * @param {Object} selectedLog - The complete selected log object
 * @returns {boolean} True if Policy Router log data exists
 */
export const hasPolicyRouterData = (selectedLog) => {
  return Boolean(selectedLog?.policyRouterLog);
};

/**
 * Check if Wrapper API data exists in log
 * @param {Object} selectedLog - The complete selected log object
 * @returns {boolean} True if Wrapper API log data exists
 */
export const hasWrapperData = (selectedLog) => {
  return Boolean(selectedLog?.policyWrapperApiLog);
};

/**
 * Check if Policy Lookup API data exists in log
 * @param {Object} selectedLog - The complete selected log object
 * @returns {boolean} True if Policy Lookup API log data exists
 */
export const hasPolicyLookupData = (selectedLog) => {
  return Boolean(selectedLog?.policyLookupApiLog);
};

/**
 * Extract summary data from selected log
 * @param {Object} selectedLog - The complete selected log object
 * @returns {Object|null} Summary data with transaction info or null
 */
export const getSummaryData = (selectedLog) => {
  if (!selectedLog) return null;
  
  return {
    transactionId: selectedLog?.transactionId || 'N/A',
    timestamp: selectedLog?.timestamp || 'N/A',
    clientName: selectedLog?.clientName || 'N/A',
    clientAbbreviation: selectedLog?.clientAbbreviation || '',
    clientNumber: selectedLog?.clientNumber || 'N/A',
    dataSource: selectedLog?.dataSource || 'N/A',
  };
};
